import React, { useState, useEffect, FC } from "react";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap";
import { Icon, Button, TooltipComponent } from "../../../components/Component";
import Swal from "sweetalert2";
import sitebreakdownMasterApis from "../../../api/master/sitebreakdownMaster";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import TransactionFlowDefaultTemplate from "../../others/transactionFlowDefaultTemplate";
import dayjs from "dayjs";

const SiteMaster: FC = () => {
  const [refreshToggle, setRefreshToggle] = useState(false);
  const { data: accessibility } = useSelector(
    (state: any) => state.accessibility
  );
  const [allowedActions, setAllowedActions] = useState<any>({
    add: 0,
    view: 0,
    edit: 0,
    approve: 0,
    reject: 0,
    cancel: 0,
  });

  useEffect(() => {
    if (accessibility) {
      try {
        const { add, view, edit, approve, reject, cancel } =
          accessibility["Master"];
        setAllowedActions({ add, view, edit, approve, reject, cancel });
      } catch (error) {
        console.log(error);
      }
    }
  }, [accessibility]);

  const navigate = useNavigate();

  const handleDelete = (id: any) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deleteSiteById(id);
      }
    });
  };

  const deleteSiteById = async (id: any) => {
    try {
      const { status, message } =
        await sitebreakdownMasterApis.deleteSiteStructureById(id);
      if (status) {
        await Swal.fire("Deleted!", message, "success");
        refreshData();
      } else {
        await Swal.fire("Error", message, "error");
      }
    } catch (error: any) {
      await Swal.fire("Error", error.message, "error");
    }
  };

  // Manual refresh function to trigger data fetch
  const refreshData = () => {
    setRefreshToggle((prev) => !prev);
  };

  const handleNavigateToForm = (id?: any) => {
    if (id) {
      navigate(`/sitebreakdown-master/edit/${id}`);
    } else {
      navigate(`/sitebreakdown-master/create`);
    }
  };

  const handleViewDetails = (id: any) => {
    navigate(`/sitebreakdown-view/${id}`);
    // navigate(`/sitesViewDetails/${id}`);
  };

  // Define select filters for the template
  const selectFilters = [
    {
      title: "Status",
      placeholder: "Select Status",
      options: [
        { label: "Active", value: "Active" },
        { label: "Inactive", value: "Inactive" },
      ],
      onChange: (value: any) => {
        console.log("Status selected:", value);
      },
    },
  ];

  const columns = [
    {
      title: "Site Structure ID",
      dataIndex: "siteStructureSequenceId",
      key: "siteStructureSequenceId",
      width: 100,
    },
    {
      title: "Site Name",
      dataIndex: "siteName",
      key: "siteName",
      width: 150,
    },
    {
      title: "Project Name",
      dataIndex: "projectName",
      key: "projectName",
      width: 200,
    },
    {
      title: "Project Type",
      dataIndex: "projectTypeName",
      key: "projectTypeName",
      width: 150,
      render: (text: any) => <span>{text}</span>,
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (text: any) => <span>{dayjs(text).format("DD-MMM-YYYY")}</span>,
    },
    {
      title: "Created By",
      dataIndex: "createdByName",
      key: "createdByName",
      width: 150,
      render: (text: any) => text || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (text: any) => {
        let color = "success";
        if (text === "InActive") color = "danger";
        if (text === "Onhold") color = "warning";

        return (
          <span className={`tb-status text-${color}`}>
            {text.replace("_", " ")}
          </span>
        );
      },
    },
    {
      title: "Details",
      dataIndex: "action",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text: any, record: any) => {
        const canView = allowedActions?.view;
        const canToggleStatus = true;
        const canDelete = allowedActions?.cancel;

        const availableActions = [canView, canToggleStatus, canDelete].filter(
          Boolean
        ).length;

        return (
          <div
            className="details-cell"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "40px",
              gap: "8px",
            }}
          >
            {canView && (
              <UncontrolledTooltip placement="top" target={`view-${record.id}`}>
                View Details
              </UncontrolledTooltip>
            )}
            {canView && (
              <a
                href="#view"
                className="btn btn-icon btn-trigger"
                id={`view-${record.id}`}
                onClick={async (ev) => {
                  ev.preventDefault();
                  handleViewDetails(record.id);
                }}
              >
                <Icon name="focus"></Icon>
              </a>
            )}

            {(canView || canDelete) && (
              <UncontrolledDropdown direction="start">
                <DropdownToggle
                  tag="button"
                  className="btn btn-sm btn-icon btn-light"
                  style={{
                    border: "none",
                    background: "none",
                    position: "relative",
                    zIndex: 1,
                    color: "#526484",
                  }}
                >
                  <Icon name="more-h" style={{ color: "inherit" }}></Icon>
                </DropdownToggle>
                <DropdownMenu
                  end
                  style={{
                    position: "fixed",
                    minWidth: "160px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    border: "1px solid #e5e5e5",
                    zIndex: 9999,
                    inset: "auto auto auto auto !important",
                  }}
                  container="body"
                >
                  <ul className="link-list-opt no-bdr" style={{ margin: 0 }}>
                    {/* View option */}
                    {canView && (
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#view"
                          onClick={async (ev) => {
                            ev.preventDefault();
                            handleViewDetails(record.id);
                          }}
                          style={{
                            padding: "8px 16px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Icon name="focus" className="me-2" />
                          <span>View Details</span>
                        </DropdownItem>
                      </li>
                    )}

                    {/* Delete option */}
                    {canDelete && (
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#delete"
                          onClick={(ev) => {
                            ev.preventDefault();
                            handleDelete(record.id);
                          }}
                          style={{
                            padding: "8px 16px",
                            display: "flex",
                            alignItems: "center",
                            textDecoration: "none",
                          }}
                        >
                          <Icon name="trash" className="me-2"></Icon>
                          <span>Delete</span>
                        </DropdownItem>
                      </li>
                    )}
                  </ul>
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="sitebreakdownmaster-list"
      title="Site Break Down Structure"
      columns={columns}
      selectFilters={selectFilters}
      extra={{
        tableScrollX: 1200,
        excelExport: true,
      }}
      refresh={refreshToggle}
      topRightJSX={
        allowedActions?.add ? (
          <Button color="primary" onClick={() => handleNavigateToForm()}>
            <Icon name="plus"></Icon>
            <span>Add</span>
          </Button>
        ) : (
          <></>
        )
      }
    />
  );
};

export default SiteMaster;
